import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import { useHistory } from 'react-router-dom'
import { PatientTypeOptions } from 'src/config/patientTypeOptions'
import routes from 'src/config/routes'
import { Button } from '@components/Button/Button'
import Footer from '@components/Footer'
import Header from '@components/Header'
import Page from '@components/Page'
import { OldPatientContent } from '@components/PhoneVerificationBody/OldPatientContent'
import { PhoneVerificationBody } from '@components/PhoneVerificationBody/PhoneVerificationBody'
import Spinner from '@components/Spinner'
import {
  useChoiceDispatch,
  useChoiceState,
  setValidatedPhoneNumber,
  setPatientReferenceIdAndPreferredPractitionerId,
} from '@context/ChoiceContext'
import { useConfig } from '@context/ConfigurationContext'
import {
  selectPatient,
  setLinkedPatients,
  setVerified,
  useVerifyPhoneContext,
  useVerifyPhoneDispatch,
} from '@context/VerifyPhoneContext'
import { useRequestReturningPatientOtpCode } from '@hooks/useRequestReturningPatientOtpCode'
import { useSelectPatientType } from '@hooks/useSelectPatientType'
import { verifyPhoneNumberForOldPatient } from 'src/services/api/verifyPhone'
import { INITAL_CODE_VALUE } from '@screens/PhoneVerification/PhoneVerification'
import useStyles from './styles'
import { useSelectPreferredPractitioner } from '@hooks/useSelectPreferredPractitioner'
import { TEST_IDS } from 'src/constants/testIds'
import { getErrorTranslationKey } from 'src/helpers/getApiErrorTranslationKey'
import { useLanguage } from '@hooks/useLanguage'
import { useTreatmentBundleIdForClinic } from '@hooks/useTreatmentBundleIdForClinic'

export const OldPatientPhoneVerificationScreen = () => {
  const { t } = useTranslation()
  const classes = useStyles()
  const history = useHistory()
  const selectPatientType = useSelectPatientType()
  const { patientType } = useChoiceState()
  const treatmentBundleTypeReferenceId = useTreatmentBundleIdForClinic()
  const choiceDispatch = useChoiceDispatch()
  const [code, setCode] = useState(INITAL_CODE_VALUE)
  const [error, setError] = useState('')
  const { phone } = useVerifyPhoneContext()
  const verifyPhoneDispatch = useVerifyPhoneDispatch()
  const { clinicId } = useConfig()
  const selectPreferredPractitioners = useSelectPreferredPractitioner({
    clearSelectedSlot: true,
    possibleToSetAll: false,
  })
  const lang = useLanguage()

  if (patientType === PatientTypeOptions.NEW) {
    history.replace(routes.patient)
  }

  const onBackClicked = () => history.push(routes.patient)

  const continueAsNewPatient = () => {
    selectPatientType(PatientTypeOptions.NEW)
    history.replace(routes.patient)
  }

  const onCodeChange = (value: string[]) => {
    setCode(value)
  }

  const clearCodeInput = () => {
    setCode(INITAL_CODE_VALUE)
  }

  const { mutate: requestOtpCode, isLoading: requestingOtpCode } = useRequestReturningPatientOtpCode({
    onError: (errorTranslationKey: string) => {
      setError(errorTranslationKey)
    },
  })

  const { mutate: verifyPhoneNumber, isLoading: verifyingPhoneNumber } = useMutation(verifyPhoneNumberForOldPatient, {
    onSuccess(patients) {
      verifyPhoneDispatch(setVerified(phone, true))
      verifyPhoneDispatch(setLinkedPatients(patients))
      choiceDispatch(setValidatedPhoneNumber(phone))

      if (patients.length > 1) {
        history.replace(routes.oldPatientSelectAccount)
      } else {
        const patient = patients[0]
        verifyPhoneDispatch(selectPatient(patient))
        choiceDispatch(
          setPatientReferenceIdAndPreferredPractitionerId({
            patientReferenceId: patient.patientReferenceId,
            preferredPractitionerReferenceId: patient.practitionerReferenceId,
          })
        )
        const preferredPractitionerId = patient.practitionerReferenceId
        selectPreferredPractitioners([preferredPractitionerId], patient?.patientReferenceId)
        history.replace(routes.appointment)
      }
    },
    onError: (error) => {
      setError(t(getErrorTranslationKey(error)))
      clearCodeInput()
    },
  })

  const requestNewSmsCode = () => {
    requestOtpCode({ phone, clinicId, lang })
    clearCodeInput()
    setError('')
  }

  const handleVerifyPhoneNumber = () => {
    verifyPhoneNumber({
      mobilePhoneNumber: phone,
      otpCode: code.join(''),
      treatmentBundleReferenceId: treatmentBundleTypeReferenceId,
      clinicReferenceId: clinicId,
    })
  }

  const isLoading = requestingOtpCode || verifyingPhoneNumber
  const bookButtonDisabled = isLoading || code.some((char) => !char)

  return (
    <Page
      header={<Header onBackClick={onBackClicked} />}
      body={
        <>
          {isLoading && <Spinner showOverlay size="large" />}
          <PhoneVerificationBody
            onCodeChange={onCodeChange}
            sendSmsCode={requestNewSmsCode}
            handleSubmit={handleVerifyPhoneNumber}
            code={code}
            phone={phone}
            content={<OldPatientContent phone={phone} />}
            errorMessage={error}
          />
        </>
      }
      footer={
        <Footer
          button={
            <div className={classes.footer}>
              <Button
                disabled={bookButtonDisabled}
                fullSize
                type="button"
                onClick={handleVerifyPhoneNumber}
                data-test={TEST_IDS.nextButton}
                text={t('cta_button_continue')}
              />
              <Button
                fullSize
                type="button"
                onClick={continueAsNewPatient}
                text={t('continue_as_new_patient')}
                variant="borderless"
                data-test={TEST_IDS.continueAsNewPatient}
              />
            </div>
          }
        />
      }
    />
  )
}
